/**
 * PWA Detection Utilities
 *
 * Provides utilities for detecting if the app is running as a PWA
 * and managing PWA-specific behavior.
 */

/**
 * Check if the app is currently running as an installed PWA
 * (in standalone mode, not in the browser)
 */
export function isRunningAsPWA(): boolean {
  // Check display-mode media query (works for standalone PWAs)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  // iOS Safari specific check (standalone property)
  const isIOSStandalone = (navigator as any).standalone === true;

  // Android TWA (Trusted Web Activity) check
  const isTWA = document.referrer.includes('android-app://');

  // Check for fullscreen mode (some PWAs use this)
  const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;

  return isStandalone || isIOSStandalone || isTWA || isFullscreen;
}

/**
 * Check if the PWA is installed (Chrome only)
 * Uses the getInstalledRelatedApps API
 */
export async function isPWAInstalled(): Promise<boolean> {
  // Use getInstalledRelatedApps if available (Chrome)
  if ('getInstalledRelatedApps' in navigator) {
    try {
      const apps = await (navigator as any).getInstalledRelatedApps();
      return apps.length > 0;
    } catch {
      return false;
    }
  }

  // Fallback: check if we're already running as PWA
  return isRunningAsPWA();
}

/**
 * Check if the browser supports PWA installation
 */
export function canInstallPWA(): boolean {
  // Check for beforeinstallprompt event support
  return 'BeforeInstallPromptEvent' in window || 'onbeforeinstallprompt' in window;
}

/**
 * Get the current display mode
 */
export function getDisplayMode(): 'browser' | 'standalone' | 'minimal-ui' | 'fullscreen' {
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return 'fullscreen';
  }
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return 'standalone';
  }
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return 'minimal-ui';
  }
  return 'browser';
}

/**
 * Check if the device is iOS
 */
export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

/**
 * Check if the device is Android
 */
export function isAndroid(): boolean {
  return /Android/.test(navigator.userAgent);
}

/**
 * Check if this is a mobile device
 */
export function isMobile(): boolean {
  return isIOS() || isAndroid() || /webOS|BlackBerry|Opera Mini|IEMobile/.test(navigator.userAgent);
}

/**
 * Get PWA installation instructions for the current platform
 */
export function getInstallInstructions(): {
  platform: 'ios' | 'android' | 'desktop' | 'unknown';
  instructions: string[];
} {
  if (isIOS()) {
    return {
      platform: 'ios',
      instructions: [
        'Tap the Share button at the bottom of the screen',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" to confirm'
      ]
    };
  }

  if (isAndroid()) {
    return {
      platform: 'android',
      instructions: [
        'Tap the menu button (three dots) in your browser',
        'Tap "Add to Home screen" or "Install app"',
        'Tap "Add" or "Install" to confirm'
      ]
    };
  }

  // Desktop browsers
  if (!isMobile()) {
    return {
      platform: 'desktop',
      instructions: [
        'Click the install icon in the address bar',
        'Or click the menu button and select "Install"',
        'Click "Install" to confirm'
      ]
    };
  }

  return {
    platform: 'unknown',
    instructions: [
      'Look for an install option in your browser menu',
      'Or add this page to your home screen'
    ]
  };
}

/**
 * Subscribe to display mode changes
 */
export function onDisplayModeChange(callback: (mode: string) => void): () => void {
  const modes = ['standalone', 'minimal-ui', 'fullscreen', 'browser'];
  const queries = modes.map(mode => window.matchMedia(`(display-mode: ${mode})`));

  const handler = () => {
    callback(getDisplayMode());
  };

  queries.forEach(query => {
    query.addEventListener('change', handler);
  });

  return () => {
    queries.forEach(query => {
      query.removeEventListener('change', handler);
    });
  };
}
